import { ApiError } from "@/lib/api";
import { effectiveRank, sourceTypeLabel } from "@/lib/integration-meta";
import type { Integration, IntegrationTestResult } from "@/types/api";

/**
 * Pure logic for the integrations data grid (#208): sorting, filtering, and
 * test-result interpretation. Kept free of React so it can be unit-tested
 * directly.
 */

export type SortKey = "name" | "type" | "status" | "last_event" | "last_poll" | "updated";
export type SortDir = "asc" | "desc";

export interface GridFilters {
  type: Integration["source_type"] | "all";
  status: Integration["health_status"] | "paused" | "all";
  errorsOnly: boolean;
}

/** Per-row outcome of a test-connection run, held in page state for the session. */
export type RowTestState =
  | { phase: "success"; result: IntegrationTestResult }
  | { phase: "warning"; result: IntegrationTestResult }
  | { phase: "error"; error: TestErrorInfo };

export interface TestErrorInfo {
  /** Short, user-safe headline. */
  primary: string;
  /** Optional actionable next step. */
  suggestion?: string;
  /** Raw backend/exception message — shown only inside an expandable region. */
  raw?: string;
  /** HTTP status when the failure was an HTTP response (absent for connectivity). */
  status?: number;
}

/** Failed checks from a test result (the ones whose `passed` is false). */
export function failedChecks(
  result: IntegrationTestResult,
): { name: string; detail: string }[] {
  return result.checks.filter((c) => !c.passed);
}

/**
 * Map a thrown test-connection error to a friendly, user-safe description.
 *
 * `ApiError` carries an HTTP `status` → code-specific guidance. Anything else
 * (a `fetch` `TypeError`, an abort/timeout, or an unexpected throw) has no
 * status and is treated as a connectivity problem. The raw message is only
 * ever surfaced inside the expandable details, never as the headline.
 */
export function describeTestError(err: unknown): TestErrorInfo {
  if (err instanceof ApiError) {
    const raw = err.detail || err.message;
    switch (err.status) {
      case 401:
        return {
          primary: "Authentication failed",
          suggestion: "Re-authorize this integration's credentials and try again.",
          raw,
          status: 401,
        };
      case 403:
        return {
          primary: "Permission denied",
          suggestion:
            "Your role, or the integration's credentials, lacks the required permissions.",
          raw,
          status: 403,
        };
      case 404:
        return {
          primary: "Integration not found",
          suggestion: "It may have been deleted — refresh the page.",
          raw,
          status: 404,
        };
      default:
        if (err.status !== undefined && err.status >= 500) {
          return {
            primary: "Server error during test",
            suggestion: "This is likely temporary — try again shortly.",
            raw,
            status: err.status,
          };
        }
        return {
          primary:
            err.status !== undefined
              ? `Test failed (HTTP ${err.status})`
              : "Test failed",
          raw,
          status: err.status,
        };
    }
  }

  // Non-ApiError: fetch network failure, timeout/abort, or unexpected throw.
  const raw = err instanceof Error ? err.message : String(err);
  return {
    primary: "Could not reach the server",
    suggestion: "Check your connection and retry.",
    raw,
  };
}

/** Does this integration count as "in error" for the errors-only filter? */
export function isErrored(
  integration: Integration,
  testState: RowTestState | undefined,
): boolean {
  if (integration.health_status === "unhealthy") return true;
  if (testState && (testState.phase === "warning" || testState.phase === "error")) {
    return true;
  }
  return false;
}

/** Apply the type / status / errors-only filters. */
export function filterIntegrations(
  list: Integration[],
  filters: GridFilters,
  testResults: Map<string, RowTestState>,
): Integration[] {
  return list.filter((i) => {
    if (filters.type !== "all" && i.source_type !== filters.type) return false;
    if (filters.status !== "all") {
      if (filters.status === "paused") {
        if (i.user_enabled !== false) return false;
      } else {
        // health_status filter only matches when integration is not user-paused
        if (!i.user_enabled || i.health_status !== filters.status) return false;
      }
    }
    if (filters.errorsOnly && !isErrored(i, testResults.get(i.id))) return false;
    return true;
  });
}

function compare(a: Integration, b: Integration, key: SortKey): number {
  switch (key) {
    case "name":
      return a.display_name.localeCompare(b.display_name, undefined, {
        sensitivity: "base",
      });
    case "type":
      return sourceTypeLabel(a.source_type).localeCompare(
        sourceTypeLabel(b.source_type),
        undefined,
        { sensitivity: "base" },
      );
    case "status":
      return effectiveRank(a) - effectiveRank(b);
    case "last_event":
      // Nulls last; otherwise sort by recency (ISO string comparison works for UTC timestamps).
      if (!a.last_event_at && !b.last_event_at) return 0;
      if (!a.last_event_at) return -1;
      if (!b.last_event_at) return 1;
      return a.last_event_at.localeCompare(b.last_event_at);
    case "last_poll": {
      // Nulls last regardless of sort direction is handled by the caller via
      // a stable secondary key; here we treat null as the lowest value.
      const av = a.last_poll_event_count;
      const bv = b.last_poll_event_count;
      if (av === null && bv === null) return 0;
      if (av === null) return -1;
      if (bv === null) return 1;
      return av - bv;
    }
    case "updated":
      return a.updated_at.localeCompare(b.updated_at);
  }
}

/**
 * Sort a copy of `list` by `key`/`dir`. Stable. For `last_poll`, rows with a
 * null event count always sink to the bottom regardless of direction, since a
 * "never polled" row isn't meaningfully "smallest" or "largest".
 */
export function sortIntegrations(
  list: Integration[],
  key: SortKey,
  dir: SortDir,
): Integration[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...list].sort((a, b) => {
    // Nulls always sink to the bottom regardless of direction.
    if (key === "last_poll") {
      const aNull = a.last_poll_event_count === null;
      const bNull = b.last_poll_event_count === null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
    }
    if (key === "last_event") {
      const aNull = !a.last_event_at;
      const bNull = !b.last_event_at;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
    }
    return sign * compare(a, b, key);
  });
}
